import { useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { Loader2, Sparkles, CalendarClock, Package, MapPin, Scale, Mail, Table2, Ban, Zap, ArrowDown, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Section = ({ icon: Icon, title, children, testId }) => (
  <section className="rounded-2xl border border-[#E9EDF7] bg-white p-4" data-testid={testId}>
    <div className="flex items-center gap-2">
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#F4F7FE]"><Icon className="h-3.5 w-3.5 text-[#2447D6]" /></span>
      <h3 className="font-['Manrope'] text-sm font-extrabold text-[#2B3674]">{title}</h3>
    </div>
    <div className="mt-2.5 space-y-1.5 text-xs leading-relaxed text-[#4A5578]">{children}</div>
  </section>
);

const Bullet = ({ children }) => <p className="flex gap-2"><span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-[#2447D6]" /><span>{children}</span></p>;

export const LeadStoreIntroModal = ({ open, config, onAcknowledged }) => {
  const { getAuthHeader } = useAuth();
  const [reachedEnd, setReachedEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setReachedEnd(true);
  };
  const jumpToEnd = () => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/lead-store/acknowledge`, {}, getAuthHeader());
      toast.success('Thanks — you\'re all set to use the Lead Store');
      onAcknowledged(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save your acknowledgement');
      setBusy(false);
    }
  };

  if (!config) return null;
  const buyable = config.packages.filter((p) => p.available && !p.coming_soon);
  const soon = config.packages.filter((p) => p.coming_soon);

  return (
    <Dialog open={open}>
      <DialogContent hideClose onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="flex max-h-[94vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[640px]" data-testid="lead-store-intro-modal">
        <DialogHeader className="shrink-0 bg-gradient-to-br from-[#0B1437] to-[#111C44] px-6 py-5 text-left text-white">
          <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#00E5FF]"><Sparkles className="h-3 w-3" /> Before you buy — one-time read</p>
          <DialogTitle className="mt-1 font-['Manrope'] text-xl font-extrabold text-white">How {config.product_name} Work</DialogTitle>
          <DialogDescription className="text-xs text-white/65">Please read every section below. Once you reach the end you can acknowledge and enter the Lead Store. You'll only see this once.</DialogDescription>
        </DialogHeader>

        <div ref={scrollRef} onScroll={onScroll} className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-[#F4F7FE] px-5 py-4" data-testid="lead-store-intro-scroll">
          <Section icon={Package} title="What you're buying" testId="intro-what">
            <Bullet>{config.product_description}</Bullet>
            <Bullet>You are purchasing a <strong>lead acquisition budget</strong>, not a fixed number of leads. Breeze runs the campaigns and routes every lead generated by your budget to you.</Bullet>
            <Bullet>Leads are <strong>exclusive to you</strong> — they are not shared or resold to other agents.</Bullet>
          </Section>

          <Section icon={CalendarClock} title="When you can purchase" testId="intro-window">
            <Bullet>The Lead Store is open <strong>every Thursday from 8:00 AM to 6:00 PM Central Time</strong>. Outside that window, purchasing is closed — even through saved links.</Bullet>
            <Bullet>Orders paid during a Thursday window enter the following week's cycle. <strong>Fulfillment begins the following Monday afternoon.</strong></Bullet>
            <Bullet><strong>One purchase per window.</strong> Once your payment goes through, purchasing is locked for you — for every package — until the next Thursday window.</Bullet>
          </Section>

          <Section icon={Scale} title="Packages & estimated quantities" testId="intro-packages">
            {buyable.map((p) => <Bullet key={p.key}><strong>{p.name}</strong> — {p.price_display} · est. {p.estimate.label}</Bullet>)}
            {soon.map((p) => <Bullet key={p.key}><strong>{p.name}</strong> — {p.price_display} · coming soon</Bullet>)}
            <Bullet>Estimates are based on today's cost per lead (CPL) of roughly <strong>${config.cpl_min}–${config.cpl_max}</strong>. {config.quantity_disclosure}</Bullet>
          </Section>

          <Section icon={MapPin} title="States & licensing" testId="intro-states">
            <Bullet>Leads come from our <strong>{config.states.length}-state campaign network</strong>: {config.states.join(', ')}.</Bullet>
            <Bullet><strong>You cannot select or exclude states.</strong> Distribution follows campaign performance and is not guaranteed to be equal across states.</Bullet>
            <Bullet>You are responsible for holding an <strong>active life insurance license</strong> in every state where you solicit or sell. Being licensed in all {config.states.length} states lets you work every lead you receive.</Bullet>
          </Section>

          <Section icon={Mail} title="How leads are delivered" testId="intro-delivery">
            <Bullet><strong>Real-time email</strong> — each new lead is emailed to you the moment it comes in, with contact info and lead details.</Bullet>
            <Bullet><span className="inline-flex items-center gap-1"><Table2 className="h-3 w-3 text-[#2447D6]" /> <strong>Dedicated Google Sheet</strong></span> — our marketing team builds a sheet for you on the Google account you provide at checkout.</Bullet>
            <Bullet>Your delivery address <strong>must be a Google account ending in @gmail.com</strong>. Double-check it at checkout — leads are sent to exactly what you enter.</Bullet>
            <Bullet>CRM integration is coming soon.</Bullet>
          </Section>

          <Section icon={Ban} title="Refunds & guarantees" testId="intro-refunds">
            <Bullet><strong>All lead purchases are final and non-refundable</strong> once paid.</Bullet>
            <Bullet>Buying leads does <strong>not guarantee</strong> appointments, sales, or commissions. Lead volume is an estimate and is not guaranteed.</Bullet>
            <Bullet>Payments are processed securely by Stripe. ATLAS never stores your card details. You'll receive an emailed receipt and can view order history on your Profile.</Bullet>
          </Section>

          <Section icon={Zap} title="Getting the most from your leads" testId="intro-tips">
            <Bullet><strong>Speed to lead wins.</strong> Call within the first 5 minutes whenever possible, and follow up a minimum of 6–8 times across calls, texts, and email.</Bullet>
            <Bullet>Work every lead over several weeks — many IUL prospects convert on later touches, not the first call.</Bullet>
            <Bullet>Log your dials, contacts, and appointments in the ATLAS Tracker so you can measure your own conversion.</Bullet>
          </Section>

          <div className="rounded-2xl border border-dashed border-[#2447D6]/40 bg-white p-4" data-testid="intro-end">
            <p className="flex items-center gap-2 text-xs font-bold text-[#2B3674]"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> You've reached the end. Confirm below to continue.</p>
          </div>
        </div>

        <div className="shrink-0 space-y-3 border-t border-[#E9EDF7] bg-white px-5 py-4">
          {!reachedEnd ? (
            <button type="button" onClick={jumpToEnd} className="flex w-full items-center justify-center gap-2 rounded-full border border-[#E9EDF7] py-2.5 text-xs font-bold text-[#707EAE] transition-colors hover:bg-[#F4F7FE]" data-testid="lead-store-intro-scroll-hint">
              <ArrowDown className="h-3.5 w-3.5 animate-bounce" /> Scroll through the whole overview to unlock the acknowledgement
            </button>
          ) : (
            <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-[#2B3674]" data-testid="lead-store-intro-ack">
              <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5" data-testid="lead-store-intro-ack-checkbox" />
              <span>I have read and understand how Breeze IUL Leads work — including the Thursday purchasing window and one-purchase-per-week limit, that I cannot select states and must be properly licensed, that quantities are estimates, that leads are delivered to the Gmail address I provide, and that all purchases are final and non-refundable.</span>
            </label>
          )}
          <button type="button" onClick={confirm} disabled={!reachedEnd || !agreed || busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#23ACD5] to-[#00E5FF] py-3 font-['Manrope'] text-sm font-extrabold text-[#06263A] shadow-[0_8px_28px_rgba(35,172,213,0.45)] transition-transform hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100" data-testid="lead-store-intro-confirm-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} I acknowledge — take me to the Lead Store
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
